/**
 * SocialPressureStrip — "Who's studying right now" surface (iter195 W3).
 *
 * Strava-style live-presence ribbon. Three personalities:
 *
 *   1) >0 studiers + spotlight rooms → green pulsing dot, count, 3 room
 *      pills with subject + headcount + "Join" CTA per pill.
 *   2) 0 studiers but ≥1 room exists → quieter "no one's studying yet —
 *      be the first" call to action.
 *   3) 0 rooms at all → quiet "Open a study room → bring a friend"
 *      suggestion (acts as discovery surface for new users).
 *
 * The component is non-blocking: data fetch is async and the strip
 * self-hides on first load to avoid a layout-shift "ghost". Once data
 * arrives, animates in.
 */
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users2, ArrowRight, Sparkles } from 'lucide-react';
import { useSocialStrip } from '../../hooks/useSocialStrip';

const PulseDot = ({ tint = 'bg-emerald-500' }) => (
  <span className="relative inline-flex h-2.5 w-2.5">
    <span className={`absolute inline-flex h-full w-full rounded-full ${tint} opacity-75 animate-ping`} />
    <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${tint}`} />
  </span>
);

const SocialPressureStrip = () => {
  const { data, isLoading } = useSocialStrip();
  if (isLoading || !data) return null;
  const rooms = data.rooms_online || {};
  const { count_rooms = 0, count_studiers = 0, spotlight = [] } = rooms;

  // ─── Case 1: someone IS studying right now ──────────────────────────
  if (count_studiers > 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-4 sm:p-5"
        data-testid="social-strip-live"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <PulseDot tint="bg-emerald-500" />
            <div>
              <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-700">Studying right now</p>
              <h3 className="font-heading text-sm sm:text-base font-semibold text-[var(--isp-heading)] leading-tight">
                <strong>{count_studiers}</strong> {count_studiers === 1 ? 'person is' : 'people are'} in {count_rooms} {count_rooms === 1 ? 'room' : 'rooms'}.
              </h3>
            </div>
          </div>
          <Link
            to="/study-rooms"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 whitespace-nowrap"
            data-testid="social-strip-browse"
          >
            Browse rooms <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
          {spotlight.map((room) => (
            <Link
              key={room.id}
              to={`/study-rooms?join=${room.id}`}
              className="flex-shrink-0 min-w-[160px] snap-start rounded-xl bg-[var(--isp-page)] border border-emerald-100 dark:border-emerald-500/30 px-3 py-2.5 hover:border-emerald-300 hover:shadow-sm transition-all group"
              data-testid={`social-strip-room-${room.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-[var(--isp-heading)] truncate">{room.subject}</p>
                  <p className="text-[10px] text-[var(--isp-muted)] flex items-center gap-1 mt-0.5">
                    <Users2 className="w-3 h-3" /> {room.participant_count} · {room.vibe}
                  </p>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-emerald-500 group-hover:translate-x-0.5 transition-transform" aria-hidden />
              </div>
            </Link>
          ))}
        </div>
      </motion.div>
    );
  }

  // ─── Case 2: rooms exist, just nobody studying right now ────────────
  if (count_rooms > 0) {
    return (
      <Link to="/study-rooms" data-testid="social-strip-empty-rooms">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-[var(--isp-page)] p-4 sm:p-5 flex items-center gap-3 hover:shadow-sm transition-shadow group"
        >
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Users2 className="w-5 h-5 text-[#002FA7]" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[var(--isp-muted)]">{count_rooms} room{count_rooms === 1 ? '' : 's'} open</p>
            <p className="text-sm font-semibold text-[var(--isp-heading)]">No one's studying right now — be the first.</p>
          </div>
          <ArrowRight className="w-4 h-4 text-[#002FA7] group-hover:translate-x-0.5 transition-transform" aria-hidden />
        </motion.div>
      </Link>
    );
  }

  // ─── Case 3: no rooms at all — discovery surface ────────────────────
  return (
    <Link to="/study-rooms" data-testid="social-strip-discover">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 dark:from-slate-800/40 to-[var(--isp-page)] p-4 sm:p-5 flex items-center gap-3 hover:shadow-sm transition-shadow group"
      >
        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-amber-600" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-amber-700">Study with friends</p>
          <p className="text-sm font-semibold text-[var(--isp-heading)]">Open a study room — invite a classmate and grind together.</p>
        </div>
        <ArrowRight className="w-4 h-4 text-amber-600 group-hover:translate-x-0.5 transition-transform" aria-hidden />
      </motion.div>
    </Link>
  );
};

export default SocialPressureStrip;
